import React, { useEffect, useState } from "react";
import { Table, Modal, Space, Input, Typography, Row, Col, Button, Checkbox, Switch, Image } from "antd";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SeoHelmet from "../common/SeoHelmet";
import ApiSelect from "../../utils/ApiSelect";
import GradeRegistrationForm from "./GradeRegistrationForm";
import LoginModal from "../Header/LoginModal";
import { useAuth } from "../../context/AuthContext";
import { linyuanApi } from "../../common/javascripts/common.js";
import api from "../../utils/api";
import DocTag from "./DocTag";
import GradeDetailModal from "./GradeDetailModal";
import ConsultationModal from "./ConsultationModal";
import { buildDocGroups } from "./GradeHelpers";
import "./DataBank.css";
import { getPinyin } from "../../utils/tools.js";

const pageSize = 10;

export default function Databank(props) {
    const { lang = 0 } = props;
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { gradeId } = useParams();
    const [grades, setGrades] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [productFilter, setProductFilter] = useState("");
    const [productName, setProductName] = useState("");
    const [companyFilter, setCompanyFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [keyword, setKeyword] = useState("");
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [gradeRegistrationVisible, setGradeRegistrationVisible] = useState(false);
    const [userCompanies, setUserCompanies] = useState([]);
    const [consultationVisible, setConsultationVisible] = useState(false);
    const [consultationRecord, setConsultationRecord] = useState(null);
    const [showAllGrades, setShowAllGrades] = useState(false);
    const [loginModalVisible, setLoginModalVisible] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        // Suppress ResizeObserver loop errors
        const resizeObserverLoopErr = (e) => {
            const msg = e.message || e;
            if (typeof msg === 'string' && (
                msg.includes('ResizeObserver loop completed with undelivered notifications') ||
                msg.includes('ResizeObserver loop limit exceeded')
            )) {
                e.stopImmediatePropagation();
                e.preventDefault();
            }
        };
        
        window.addEventListener('error', resizeObserverLoopErr);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
            window.removeEventListener('error', resizeObserverLoopErr);
        };
    }, []);

    useEffect(() => {
        if (productFilter) {
            handleSearch();
        }
    }, [productFilter, showAllGrades]);

    useEffect(() => {
        // loadData(1);
    }, []);

    useEffect(() => {
        if (user && user.id) {
            api.get(`/users/${user.id}`).then(res => {
                // api.js response interceptor returns res.data, so res here is the data object
                if (res && res.code === 200 && res.data) {
                    setUserCompanies(res.data.companies || []);
                }
            }).catch(err => {
                console.error("Failed to fetch user companies", err);
                setUserCompanies([]);
            });
        } else {
            setUserCompanies([]);
        }
    }, [user]);

    useEffect(() => {
        if (gradeId) {
            // Load detail if gradeId is present in URL
            const fetchDetail = async () => {
                try {
                    const res = await linyuanApi.get(`/grades/${gradeId}`);
                    const detailData = res.data || res;
                    // DetailData has an inner 'data' property containing the actual record fields
                    const record = detailData.data || detailData;
                    setDetailRecord(record);
                    setDetailVisible(true);
                } catch (e) {
                    console.error("Failed to fetch grade detail", e);
                }
            };
            fetchDetail();
        } else {
            setDetailVisible(false);
            setDetailRecord(null);
        }
    }, [gradeId]);

    const loadData = async (page, extraParams = {}) => {
        try {
            setLoading(true);
            const params = {};
            params.currentPage = page;
            params.pageSize = pageSize;
            if (keyword) {
                params.search = keyword;
            }
            // Use explicit productId if provided (to handle async state updates), otherwise use state
            const activeProductId = extraParams.productId !== undefined ? extraParams.productId : productFilter;
            if (activeProductId) {
                params.productId = activeProductId;
            }
            if (companyFilter) {
                params.companyIds = companyFilter;
            }
            if (!showAllGrades) {
                params.isRegular = 1;
            }
            const res = await linyuanApi.get("/grades", { params });
            const apiData = res || {};
            const pageData = apiData.data || apiData;
            setGrades(pageData.list || []);
            setTotal(pageData.totalCount || 0);
            setCurrentPage(pageData.currentPage || page);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadData(1);
    };

    const handlePageChange = (page) => {
        loadData(page);
    };

    const openDetail = (record) => {
        const company = getPinyin(record.company_alias || "", 1);
        const product = record.product_alias || "";
        const grade = getPinyin(record.name || "", 1);
        const slug = `${company}_${product}_${grade}`;
        const basePath = lang === 0 ? "/databank_cn" : "/databank_en";
        navigate(`${basePath}/grade/${record.id}/${slug}`);
    };

    const handlePreview = (href) => {
        setPreviewImage(href);
        setPreviewVisible(true);
    };

    const openConsultation = (record) => {
        setConsultationRecord(record);
        setConsultationVisible(true);
    };

    const columns = [
        {
            title: lang === 0 ? "牌号 / 产品" : "Grade / Product",
            key: "grade_product",
            width: isMobile ? 100 : 120,
            render: (_, record) => {
                const isLocked = record.is_regular === 0 && (!user || !user.id);
                return (
                    <div className={`databank-grade-product ${isLocked ? 'databank-cell-blur' : ''}`} style={{position: 'relative'}}>

                        <div className="databank-grade-text" style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => !isLocked && openDetail(record)}>
                            {lang === 0 ? record.name || "-" : getPinyin(record.name || "", 1)}
                        </div>
                        <div className="databank-product-text">
                            {record.product_alias || "-"}
                        </div>
                    </div>
                );
            },
        },
        {
            title: lang === 0 ? "公司 / 品牌" : "Company / Brand",
            key: "company_brand",
            width: isMobile ? 100 : 160,
            render: (_, record) => {
                const company = lang === 0 ? record.company_alias || "-" : getPinyin(record.company_alias || "", 1);
                const brand = lang === 0 ? record.brand_alias || "-" : getPinyin(record.brand_alias || "", 1);
                const logoUrl = record.company_logo_url;
                const initials = (record.company_alias || "").charAt(0) || "-";
                const isLocked = record.is_regular === 0 && (!user || !user.id);
                
                return (
                    <div className={`databank-company-brand-cell ${isLocked ? 'databank-cell-blur' : ''}`} style={{position: 'relative'}}>

                        <div className="databank-logo-wrapper">
                            {logoUrl ? (
                                <Image
                                    src={logoUrl}
                                    alt={company}
                                    className="databank-logo-image"
                                    // preview={{
                                    //     mask: false
                                    // }}
                                />
                            ) : (
                                <div className="databank-logo-placeholder">
                                    {initials}
                                </div>
                            )}
                        </div>
                        {!isMobile && (
                            <div className="databank-company-brand-text" style={{ maxWidth: '6em', overflow: 'hidden' }}>
                                <div className="databank-company-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={company}>{company}</div>
                                <div className="databank-brand-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={brand}>{brand}</div>
                            </div>
                        )}
                    </div>
                );
            },
        },
        ...(!isMobile ? [{
            title: lang === 0 ? "特性" : "Features",
            key: "features",
            width: 200,
            render: (_, record) => {
                const features = Array.isArray(record.features) ? record.features.slice(0, 3) : [];
                const isLocked = record.is_regular === 0 && (!user || !user.id);
                
                return (
                    <div className={`databank-features-cell ${isLocked ? 'databank-cell-blur' : ''}`} style={{position: 'relative'}}>
                        {isLocked && (
                            <div className="databank-cell-overlay" onClick={() => setLoginModalVisible(true)}>
                                {lang === 0 ? "特殊牌号查看请登录" : "Login to view special grades"}
                            </div>
                        )}
                        {features.map((feature, index) => (
                            <div key={index} className="databank-feature-item">
                                <span className="databank-parameter-text">{lang === 0 ? feature.nameCn : feature.nameEn}: </span>
                                <span className="databank-parameter-data">{feature.parameter || "-"}</span>
                                <span className="databank-parameter-text">{" " + feature.unit || "-"}</span>
                            </div>
                        ))}
                        {features.length < 3 && Array.from({ length: 3 - features.length }).map((_, index) => (
                            <div key={index + features.length} className="databank-feature-item"></div>
                        ))}
                    </div>
                );
            },
        }] : []),
        {
            title: lang === 0 ? "资料" : "Documents",
            dataIndex: "documents",
            key: "documents",
            width: isMobile ? 100 : 550,
            render: (docs, record) => {
                const list = Array.isArray(docs) ? docs : [];
                const { regularDocs, otherDocs } = buildDocGroups(list);
                const isLocked = record.is_regular === 0 && (!user || !user.id);
                
                return (
                    <div className={`databank-documents-cell ${isLocked ? 'databank-cell-blur' : ''}`} style={{position: 'relative'}}>
                        {isLocked && (
                            <div className="databank-cell-overlay" onClick={() => setLoginModalVisible(true)}>
                                {lang === 0 ? "登录查看" : "Login"}
                            </div>
                        )}
                        <div className="databank-documents-sections">
                            <div className="databank-documents-section">
                                <span className="databank-documents-section-label">
                                    {isMobile ? '' : lang === 0 ? "常规档案" : "Regular"}
                                </span>
                                <div className="databank-documents-list">
                                    {regularDocs.map((d) => (
                                        <DocTag key={d.key} doc={d} onPreview={!isLocked ? handlePreview : undefined} />
                                    ))}
                                </div>
                            </div>
                            <div className="databank-documents-section">
                                <span className="databank-documents-section-label">
                                    {isMobile ? '' : lang === 0 ? "其他档案" : "Others"}
                                </span>
                                <div className="databank-documents-list">
                                    {otherDocs.map((d) => {
                                        const href = d.uri;
                                        if (!href) {
                                            return null;
                                        }
                                        return <DocTag key={d.key} doc={d} onPreview={!isLocked ? handlePreview : undefined} />;
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: lang === 0 ? "操作" : "Action",
            key: "action",
            fixed: 'right',
            width: isMobile ? 80 : 100,
            render: (_, record) => {
                const isLocked = record.is_regular === 0 && (!user || !user.id);
                return (
                    <div style={{position: 'relative', height: '100%', minHeight: '40px', display: 'flex', alignItems: 'center'}}>
                        <Button type="link" onClick={() => openConsultation(record)}>
                            {lang === 0 ? "实时联络" : "FastCheckIt!"}
                        </Button>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="databank-container">
            {/* Only render general SEO if detail modal is NOT open, to avoid conflict */}
            {!detailVisible && (
                <SeoHelmet 
                    titleCn="产品档案馆" 
                    titleEn="Product Document Bank" 
                    lang={lang} 
                    extraKeywords={lang === 0 ? ["技术资料", "TDS", "COA", "SDS"] : ["Technical Data Sheet", "TDS", "COA", "SDS"]}
                />
            )}
            <div className="databank-header">
                <Typography.Title level={2}>
                    {lang === 0 ? "产品档案馆" : "Product Document Bank"}
                </Typography.Title>
                <Typography.Paragraph>
                    {lang === 0
                        ? "根据产品、公司和牌号查询对应的技术资料与生产资料。"
                        : "Search technical and production documents by product, company and grade."}
                </Typography.Paragraph>
            </div>
            <div className="databank-filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <ApiSelect
                            apiUrl="/products"
                            labelKey="alias"
                            valueKey="id"
                            pageSize={pageSize}
                            placeholder={
                                lang === 0 ? "按产品筛选" : "Select by products"
                            }
                            allowClear={false}
                            value={productFilter}
                            onChange={(val, option) => {
                                setProductFilter(val);
                                if (option) setProductName(option.label);
                            }}
                            onOptionsLoaded={(opts) => {
                                if (!productFilter && opts && opts.length > 0) {
                                    setProductFilter(opts[0].value);
                                    setProductName(opts[0].label);
                                }
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <ApiSelect
                            apiUrl="/brands"
                            labelKey="alias"
                            searchKey="search"
                            valueKey="company_id"
                            pageSize={pageSize}
                            params={{ productId: productFilter }}
                            placeholder={
                                lang === 0 ? "按生产单位筛选" : "Select by manufacturers"
                            }
                            onChange={(val) => {
                                setCompanyFilter(val);
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Input
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder={lang === 0 ? "按牌号名称搜索" : "Search by grade name"}
                            allowClear
                            addonAfter={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ marginRight: '8px', fontSize: '12px' }}>{lang === 0 ? "特殊牌号" : "All"}</span>
                                    <Switch
                                        size="small"
                                        checked={showAllGrades}
                                        onChange={(checked) => setShowAllGrades(checked)}
                                    />
                                </div>
                            }
                        />
                    </Col>
                    <Col xs={24}>
                        <Space>
                            <a className="databank-search-button" onClick={handleSearch}>
                                {lang === 0 ? "查询" : "Search"}
                            </a>
                            <Button 
                                type="primary" 
                                onClick={() => setGradeRegistrationVisible(true)}
                                disabled={!user}
                            >
                                {lang === 0 ? "登记本公司牌号" : "Register Company Grade"}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>
            <Table
                rowKey={(record) => record.id}
                columns={columns}
                dataSource={grades}
                loading={loading}
                scroll={{ x: isMobile ? 450 : undefined }}
                pagination={{
                    current: currentPage,
                    pageSize,
                    total,
                    onChange: handlePageChange,
                }}
            />
            <GradeDetailModal
                open={detailVisible}
                onCancel={() => {
                    setDetailVisible(false);
                    const basePath = lang === 0 ? "/databank_cn" : "/databank_en";
                    navigate(basePath);
                }}
                record={detailRecord}
                lang={lang}
                onPreview={handlePreview}
                userCompanies={userCompanies}
                user={user}
            />
            <Modal
                open={previewVisible}
                footer={null}
                onCancel={() => {
                    setPreviewVisible(false);
                    setPreviewImage("");
                }}
                width={800}
                bodyStyle={{ textAlign: "center" }}
            >
                {previewImage && (
                    <img
                        src={previewImage}
                        alt={lang === 0 ? "文档预览" : "Preview"}
                        className="databank-preview-image"
                    />
                )}
            </Modal>
            <Modal
                open={gradeRegistrationVisible}
                title={lang === 0 ? "登记新牌号" : "Register New Grade"}
                onCancel={() => setGradeRegistrationVisible(false)}
                footer={null}
                width={600}
            >
                <GradeRegistrationForm
                    visible={gradeRegistrationVisible}
                    onCancel={() => setGradeRegistrationVisible(false)}
                    onSubmit={handleSearch}
                    currentProductId={productFilter}
                    currentProductName={productName}
                    user={user}
                    lang={lang}
                />
            </Modal>
            <ConsultationModal
                visible={consultationVisible}
                onCancel={() => setConsultationVisible(false)}
                record={consultationRecord}
                lang={lang}
                user={user}
                userCompanies={userCompanies}
            />
            <LoginModal 
                visible={loginModalVisible}
                onCancel={() => setLoginModalVisible(false)}
            />
        </div>
    );
}
